import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  InputNumber,
  Modal,
  Space,
  Steps,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { BasicHolding } from '../../services/basicBookApi';
import {
  GridImportRequest,
  ImportLevel,
  ImportPreview,
  applyGridImport,
  previewGridImport,
} from '../../services/gridImportApi';
import { importInProgressOf } from '../../services/importJobApi';
import ImportJobProgress from '../ImportJobProgress';
import { Field, FieldGrid, NUMBER_INPUT_STYLE, ReadonlyValue, RightAlignedInputs } from '../ImportForm';
import { formatDecimal, formatInt, numberInputProps } from '../../utils/numberFormat';

const { Text } = Typography;

/**
 * Claiming shares back out of the basic book and into a grid.
 *
 * TWO SCREENS, and the split is the design rather than pagination. The first asks what the
 * user HOLDS — a fact they can check against their broker statement. The second asks what
 * grid they want it in — a choice with consequences. Mixing them put a number they were
 * reporting next to numbers they were deciding, and the cost basis is the one figure here
 * that nothing else can supply.
 *
 * WHY THE SERVER PRICES IT. Every level, every warning and every refusal comes from
 * `preview`, and `apply` recomputes the same plan instead of trusting what came back. The
 * arithmetic lives in one place, so what this screen shows and what gets written cannot
 * disagree — and a stale preview left open in a tab cannot be what lands.
 *
 * THE TWO WARNINGS are opposite mistakes and neither blocks:
 *
 *   ladder below the cost   every take-profit sells at a loss, because TP is measured from
 *                           the LEVEL price and never from what the shares cost
 *   ladder above the market the empty levels are buy limits above the market, and a buy
 *                           limit above the market fills AT the market — the whole capacity
 *                           spent on the first tick
 *
 * Avoiding one walks into the other, so both are shown in money and the user decides. A
 * mistake in the ladder is expensive; being unable to say "yes, I am cutting this loss on
 * purpose" is worse.
 */

/** Raw VND per tick, mirroring the server's TICK_VND (0.1 thousand VND). */
const TICK_VND = 100;

interface Props {
  open: boolean;
  dataConfigId?: string | null;
  /** The basic-book row being claimed. Its quantity is the ceiling on the import. */
  holding: BasicHolding | null;
  /** Latest market price, raw VND. Omitted and the "buys immediately" warning is not raised. */
  marketPrice?: number | null;
  onClose: () => void;
  onImported?: () => void;
}

const GridImportModal: React.FC<Props> = ({
  open,
  dataConfigId,
  holding,
  marketPrice,
  onClose,
  onImported,
}) => {
  const [step, setStep] = useState(0);
  const [quantity, setQuantity] = useState<number>(0);
  // Cash beside the shares: without it the grid can sell the import but never buy back.
  const [cash, setCash] = useState<number>(0);
  const [origin, setOrigin] = useState<number>(0);
  const [levelCount, setLevelCount] = useState<number>(5);
  const [intervalTicks, setIntervalTicks] = useState<number>(1);
  /**
   * Per-level quantities once the user has touched them. Null means "use the even spread",
   * which is not the same as an array that happens to equal it: re-deriving the spread when
   * the level count changes is right, silently rewriting an edit is not.
   */
  const [quantities, setQuantities] = useState<number[] | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  // the background job the import became; set, the modal shows its progress instead of the form
  const [importId, setImportId] = useState<string | null>(null);
  const [importDone, setImportDone] = useState(false);
  const [attachedNote, setAttachedNote] = useState<string | null>(null);

  // Reset on open rather than on close: a modal that clears while it is fading out shows the
  // user their numbers vanishing.
  useEffect(() => {
    if (!open || !holding) return;
    setStep(0);
    setQuantity(holding.quantity);
    setCash(0);
    // on a tick, as the Upper Bound field shows it
    setOrigin(Math.round((marketPrice ?? holding.averagePrice ?? 0) / TICK_VND) * TICK_VND);
    setLevelCount(5);
    setIntervalTicks(1);
    setQuantities(null);
    setPreview(null);
    setImportId(null);
    setImportDone(false);
    setAttachedNote(null);
  }, [open, holding, marketPrice]);

  // the broker's cost of the unclaimed shares: shown, never typed
  const brokerAverage = holding?.averagePrice ?? null;

  const request = useMemo<GridImportRequest | null>(() => {
    if (!holding || quantity <= 0 || brokerAverage == null || origin <= 0) return null;
    return {
      symbol: holding.symbol,
      exchange: holding.exchange,
      quantity,
      origin,
      levelCount,
      intervalTicks,
      // take-profit is not sent: the server derives it from the gap, as for every grid
      ...(marketPrice ? { marketPrice: Math.round(marketPrice) } : {}),
      ...(quantities ? { quantities } : {}),
      ...(cash > 0 ? { cash } : {}),
    };
  }, [
    holding,
    quantity,
    brokerAverage,
    origin,
    levelCount,
    intervalTicks,
    marketPrice,
    cash,
    quantities,
  ]);

  const runPreview = useCallback(async () => {
    if (!dataConfigId || !request) return;
    setLoading(true);
    try {
      setPreview(await previewGridImport(dataConfigId, request));
    } catch (e: any) {
      message.error(e?.response?.data?.message ?? 'Could not price this ladder.');
    } finally {
      setLoading(false);
    }
  }, [dataConfigId, request]);

  // Debounced, because every keystroke in a number field is a render and the preview is a
  // round trip. 350ms is under the pause between deliberate edits and above a held arrow key.
  useEffect(() => {
    if (step !== 1 || !request) return;
    const t = setTimeout(() => void runPreview(), 350);
    return () => clearTimeout(t);
  }, [step, request, runPreview]);

  const setLevelQty = (index: number, value: number) => {
    const base = quantities ?? (preview?.levels ?? []).map((l) => l.quantity);
    const next = [...base];
    next[index] = Math.max(0, Math.round(value));
    setQuantities(next);
  };

  const apply = async () => {
    if (!dataConfigId || !request) return;
    setApplying(true);
    try {
      const { importId: id } = await applyGridImport(dataConfigId, request);
      setImportId(id);
    } catch (e: any) {
      // an import on this grid is still open: follow that one rather than refusing blind
      const open409 = importInProgressOf(e);
      if (open409) {
        setAttachedNote(open409.message);
        setImportId(open409.importId);
        return;
      }
      // The server's refusal carries the reason — a running bot, a restricted symbol, a plan
      // that does not add up. Replacing it with a generic line loses the only useful part.
      message.error(e?.response?.data?.message ?? 'The import was refused.');
    } finally {
      setApplying(false);
    }
  };

  const blocked = (preview?.problems.length ?? 0) > 0;
  const spent = useMemo(
    () => (preview?.levels ?? []).reduce((sum, l) => sum + l.quantity, 0),
    [preview],
  );

  const NUM: React.CSSProperties = { fontVariantNumeric: 'tabular-nums' };
  const columns: ColumnsType<ImportLevel> = [
    {
      title: 'Price',
      dataIndex: 'price',
      align: 'right',
      // thousand VND, as the grid settings' ladder shows it
      render: (v: number) => <Text style={NUM}>{formatDecimal(v / 1000, 1)}</Text>,
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      align: 'right',
      width: 150,
      render: (v: number, _row, i) => (
        <InputNumber
          size="small"
          min={0}
          step={100}
          value={v}
          onChange={(x) => setLevelQty(i, Number(x ?? 0))}
          style={{ width: '100%', ...NUM }}
        />
      ),
    },
    {
      title: 'Sells At',
      dataIndex: 'takeProfitPrice',
      align: 'right',
      render: (v: number) => <Text style={NUM}>{formatDecimal(v / 1000, 1)}</Text>,
    },
    {
      title: 'P/L per Share (VND)',
      dataIndex: 'pnlPerShare',
      align: 'right',
      // Measured against the broker's cost: a negative number here is a level that sells at a
      // loss by design.
      render: (v: number) => (
        <Text type={v < 0 ? 'danger' : 'success'} style={NUM}>
          {v > 0 ? '+' : ''}
          {formatInt(v)}
        </Text>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={720}
      title={`Import ${holding?.symbol ?? ''} into this grid`}
      footer={
        importId ? (
          <Button type={importDone ? 'primary' : 'default'} onClick={onClose}>
            {importDone ? 'Done' : 'Close'}
          </Button>
        ) : step === 0 ? (
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              disabled={quantity <= 0 || brokerAverage == null}
              onClick={() => setStep(1)}
            >
              Next: choose the grid
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={() => setStep(0)}>Back</Button>
            <Button
              type="primary"
              loading={applying}
              disabled={blocked || !preview || loading}
              onClick={apply}
            >
              Import
            </Button>
          </Space>
        )
      }
    >
      {importId ? (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {attachedNote && <Alert type="warning" showIcon message={attachedNote} />}
          <Text type="secondary" style={{ fontSize: 12 }}>
            The import runs in the background: the grid and the bot change together, or neither
            does. Closing this window does not stop it.
          </Text>
          <ImportJobProgress
            importId={importId}
            onReady={() => {
              setImportDone(true);
              if (!attachedNote && request) {
                message.success(`${request.symbol}: ${formatInt(request.quantity)} share(s) imported.`);
              }
              onImported?.();
            }}
          />
        </Space>
      ) : (
      <>
      <Steps
        size="small"
        current={step}
        items={[{ title: 'What you hold' }, { title: 'The grid it goes into' }]}
        style={{ marginBottom: 16 }}
      />

      {step === 0 && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Your broker holds <Text strong>{formatInt(holding?.quantity ?? 0)}</Text> {holding?.symbol} that no
            strategy claims. Nothing moves at the broker — importing only starts the grid claiming them again.
          </Text>

          <FieldGrid columns={2}>
            <Field
              label="Shares to import"
              error={holding && quantity > holding.quantity ? `Only ${formatInt(holding.quantity)} are unclaimed.` : undefined}
              help={holding ? `Up to ${formatInt(holding.quantity)}, in lots of 100.` : undefined}
            >
              <InputNumber
                {...numberInputProps()}
                min={1}
                max={holding?.quantity}
                step={100}
                value={quantity}
                onChange={(v) => setQuantity(Number(v ?? 0))}
                style={NUMBER_INPUT_STYLE}
              />
            </Field>
            <Field
              label="Cash to add (VND)"
              help="What the empty levels buy with. Must fit the account's free cash."
            >
              <InputNumber
                {...numberInputProps()}
                min={0}
                step={1_000_000}
                value={cash}
                onChange={(v) => setCash(Number(v ?? 0))}
                style={NUMBER_INPUT_STYLE}
              />
            </Field>
          </FieldGrid>

          <Field
            label="Average price (VND per share)"
            help={
              brokerAverage != null
                ? "The broker's cost of these shares. Every profit figure for this symbol is measured from it."
                : 'The broker reports no cost for these shares, so they cannot be imported yet.'
            }
          >
            <ReadonlyValue strong>{brokerAverage != null ? formatInt(Math.round(brokerAverage)) : '—'}</ReadonlyValue>
          </Field>
        </Space>
      )}

      {step === 1 && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {/* Same names and unit as the grid's own settings: prices in thousand VND, one
              decimal (the 0.1 tick), and No. of Intervals = levels − 1. The request is still
              raw VND / ticks / level count; the conversion happens here, at the field. */}
          <FieldGrid columns={3}>
            <Field label="Upper Bound">
              <InputNumber
                min={0.1}
                step={0.1}
                precision={1}
                value={origin / 1000}
                onChange={(v) => setOrigin(Math.round(Number(v ?? 0) * 10) * TICK_VND)}
                style={NUMBER_INPUT_STYLE}
              />
            </Field>
            <Field label="Grid Interval" hint="Take-profit is the interval: each level sells at the level above it.">
              <InputNumber
                min={0.1}
                step={0.1}
                precision={1}
                value={intervalTicks / 10}
                onChange={(v) => setIntervalTicks(Math.max(1, Math.round(Number(v ?? 0.1) * 10)))}
                style={NUMBER_INPUT_STYLE}
              />
            </Field>
            <Field label="No. of Intervals">
              <InputNumber
                min={1}
                max={199}
                precision={0}
                value={levelCount - 1}
                onChange={(v) => {
                  setLevelCount(Math.max(1, Number(v ?? 1)) + 1);
                  // A new level count means a new spread. Keeping the old array would leave
                  // shares on levels that no longer exist.
                  setQuantities(null);
                }}
                style={NUMBER_INPUT_STYLE}
              />
            </Field>
          </FieldGrid>

          <Text type="secondary" style={{ fontSize: 12 }}>
            Shares are spread evenly in whole lots, remainder to the lowest levels. Change any row — the total
            has to come back to <Text strong>{formatInt(quantity)}</Text>, because every share needs a level.
          </Text>

          <RightAlignedInputs>
          <Table<ImportLevel>
            size="small"
            rowKey={(r) => r.price}
            loading={loading}
            pagination={false}
            // only a long ladder scrolls; the total row stays pinned under it
            scroll={(preview?.levels.length ?? 0) > 8 ? { y: 320 } : undefined}
            dataSource={preview?.levels ?? []}
            columns={columns}
            summary={() => (
              <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <Text strong>Total</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong type={spent === quantity ? undefined : 'danger'} style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatInt(spent)} / {formatInt(quantity)}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} colSpan={2} />
              </Table.Summary.Row>
              </Table.Summary>
            )}
          />
          </RightAlignedInputs>

          {(preview?.warnings ?? []).map((w) => (
            <Alert
              key={w.kind}
              type="warning"
              showIcon
              message={
                w.kind === 'SELLS_AT_A_LOSS'
                  ? 'This grid takes profit below what you paid'
                  : 'This grid buys as soon as it starts'
              }
              description={
                <Text style={{ fontSize: 12 }}>
                  {w.detail}
                  {/* Not a refusal. Cutting a loss on purpose, or gathering a position in
                      one go, are both things a user is allowed to mean. */}
                  <br />
                  <Text type="secondary">
                    You can go ahead — this is here so the number is not a surprise.
                  </Text>
                </Text>
              }
            />
          ))}

          {(preview?.problems ?? []).map((p) => (
            <Alert key={p.field} type="error" showIcon message={p.message} />
          ))}
        </Space>
      )}
      </>
      )}
    </Modal>
  );
};

export default GridImportModal;
